import React from 'react';
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { LegalPageLayout, LegalSection } from 'components/LegalPageLayout';
import BulletList from 'components/BulletList';
import NumberedList from 'components/NumberedList';

// ==============================|| DATA RETENTION POLICY ||============================== //
// Source: KiT Contact Data Retention Policy, Sterling Systems Private Limited,
// Version 1.0, effective/last updated 19 July 2026.
// NOTE: one reference in the source text is still an open placeholder — the
// in-app deletion path/URL (Section 6). Rendered below as plain bracketed
// text rather than a real link until that destination exists.

const PRINCIPLES = [
  'Retain data only for a documented business or legal purpose.',
  'Use the shortest reasonable retention period consistent with that purpose.',
  'Allow authorised deletion and account closure workflows.',
  'Suspend deletion when a lawful preservation hold applies.',
  'Apply deletion to active systems and allow residual backup copies to expire through controlled rotation.',
  'Review retention periods at least annually and when law, product functionality or risk changes.'
];

const RETENTION_SCHEDULE = [
  {
    type: 'Account profile',
    rule: 'For the duration of the active account and normally up to 90 days after account deletion, unless a longer period is legally required or needed to resolve a dispute.'
  },
  {
    type: 'User contact database and relationship records',
    rule: 'Until deleted by the user, account owner or authorised administrator; following account deletion, normally removed from active systems within 90 days and from routine backups through the applicable backup rotation.'
  },
  {
    type: 'Consent and policy acceptance records',
    rule: 'For the account duration and up to 8 years thereafter where reasonably necessary to demonstrate compliance, resolve disputes or meet legal obligations.'
  },
  {
    type: 'Billing and tax records',
    rule: 'For the period required under applicable tax, accounting and company laws, commonly up to 8 financial years or such other statutory period.'
  },
  {
    type: 'Security and audit logs',
    rule: 'Normally 180 days to 2 years, depending on risk, security needs and legal requirements.'
  },
  {
    type: 'Support records',
    rule: 'Normally up to 3 years after closure of the request, unless longer retention is necessary for a dispute or legal obligation.'
  }
];

const EVENT_BASED_RETENTION = [
  'Account closure: initiate deletion or anonymisation of account content after identity verification and completion of contractual or legal steps.',
  'Purpose completion: delete personal data when the stated purpose is no longer served and no legal retention ground remains.',
  'Consent withdrawal: stop the affected consent-based processing and delete data not supported by another lawful ground.',
  'Inactive accounts: provide notice before erasure where required by applicable law or the notified inactivity standard. The production system must implement the legally applicable notice period for the relevant class of Data Fiduciary.',
  'Dispute, fraud or security investigation: retain relevant records until the matter and applicable limitation period have concluded.',
  'Legal hold: suspend normal deletion for records reasonably related to anticipated or pending litigation, regulatory inquiry, audit or lawful direction.'
];

const ROLES_GOVERNANCE = [
  'Product owner: maintains the product data inventory and maps each data field to a purpose and retention period.',
  'Engineering / operations: implements automated deletion, backup rotation, access controls and deletion verification.',
  'Support: verifies and routes user deletion requests.',
  'Privacy / grievance contact: reviews exceptions, complaints, legal changes and retention holds.',
  'Finance / legal: defines statutory retention for tax, accounting, contracts and disputes.'
];

const DataRetentionPolicy = () => (
  <LegalPageLayout title="Data Retention Policy" lastUpdated="19 July 2026">
    <LegalSection title="1. Objective">
      This Policy defines how Sterling Systems Private Limited retains and deletes personal data and other records
      processed through KiT Contact. Its objectives are data minimisation, storage limitation, legal compliance,
      security, reliable service and defensible deletion.
    </LegalSection>

    <LegalSection title="2. Principles">
      <NumberedList items={PRINCIPLES} />
    </LegalSection>

    <LegalSection title="3. Retention Schedule">
      <TableContainer component={Paper} variant="outlined" sx={{ my: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '30%' }}>Data / record type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Default retention and deletion rule</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {RETENTION_SCHEDULE.map((row) => (
              <TableRow key={row.type}>
                <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>{row.type}</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>{row.rule}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </LegalSection>

    <LegalSection title="4. Event-Based Retention">
      <BulletList items={EVENT_BASED_RETENTION} />
    </LegalSection>

    <LegalSection title="5. Deletion Methods">
      Deletion may include secure logical deletion, cryptographic erasure, overwriting, physical destruction,
      irreversible aggregation or anonymisation appropriate to the system and risk. Production databases should use
      documented deletion jobs, access controls and audit logs. Backups should not ordinarily be restored merely to
      delete a single record; if restored, the deletion instruction must be reapplied before normal use.
    </LegalSection>

    <LegalSection title="6. User Requests">
      Users may request deletion through [IN-APP DELETION PATH / URL] or{' '}
      <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>. We may verify identity, authority and
      account ownership. We will respond within the legally applicable period. We may decline or limit deletion when
      retention is required by law, necessary for a legal claim, required to protect security, or otherwise lawfully
      permitted, and will explain the basis where required.
    </LegalSection>

    <LegalSection title="7. Business Customer Responsibilities">
      Where a business customer controls personal data entered into the service, that customer must configure and
      apply an appropriate retention schedule, respond to its data principals and instruct Sterling Systems through
      authorised channels. Sterling Systems will act on documented deletion instructions subject to the service
      agreement and law.
    </LegalSection>

    <LegalSection title="8. Roles and Governance">
      <BulletList items={ROLES_GOVERNANCE} />
    </LegalSection>

    <LegalSection title="9. Review and Exceptions">
      Exceptions require documented approval from the privacy or legal contact, a specific purpose, a review date and
      appropriate access restrictions. This Policy will be reviewed at least annually and after a material product,
      vendor or legal change.
    </LegalSection>

    <LegalSection title="10. Contact and Jurisdiction">
      Questions should be sent to <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>. This Policy is
      governed by Indian law. Subject to mandatory statutory forums, courts of competent jurisdiction in Pune,
      Maharashtra shall have exclusive jurisdiction.
    </LegalSection>
  </LegalPageLayout>
);

export default DataRetentionPolicy;
